import type { Rng, RoundOutcome } from '../../core/contracts'

/**
 * Snowflake - зрительное восприятие и анализ формы.
 *
 * Снежинка состоит из шести одинаковых лучей, повёрнутых вокруг центра.
 * Один сектор пустой - нужно понять, какой из вариантов туда подходит.
 *
 * Задача на восприятие, а не на память: образец остаётся перед глазами,
 * запоминать нечего. Трудность в том, что варианты отличаются всё
 * мельче - на верхних уровнях меняется одна ветка на несколько градусов.
 *
 * Только геометрия: ни цвета, ни текста. Игра не зависит от языка
 * и доступна при нарушениях цветовосприятия.
 */

/** Ветка на луче: положение вдоль оси, длина и угол отхода. */
export interface Branch {
  /** Доля длины луча от центра, 0..1 */
  readonly at: number
  /** Длина ветки в долях луча */
  readonly len: number
  /** Угол отхода от оси, градусы */
  readonly angle: number
}

export type TipKind = 'plain' | 'fork' | 'diamond'

export interface Arm {
  readonly branches: readonly Branch[]
  readonly tip: TipKind
}

export interface SnowflakeTrial {
  /** Правильный луч - тот, что повторён в пяти видимых секторах */
  readonly arm: Arm
  /** Варианты ответа */
  readonly options: readonly Arm[]
  /** Индекс правильного варианта */
  readonly correctIndex: number
  /** Какой из шести секторов пустует, 0..5 */
  readonly missingSector: number
}

export interface SnowflakeRound {
  readonly trials: readonly SnowflakeTrial[]
  /** Лимит на пробу; не успел - засчитывается как ошибка */
  readonly timeLimitMs: number
}

export interface SnowflakeAnswer {
  /** Индекс выбранного варианта на каждой пробе; null - не успел */
  readonly picks: readonly (number | null)[]
  /** Среднее время верных ответов, мс */
  readonly rtMs: number
}

export interface LevelSpec {
  readonly branches: number
  readonly trials: number
  readonly optionCount: number
  /**
   * Насколько сильно отличаются неверные варианты, 0..1.
   * Главная ось трудности: 1 - разница бросается в глаза,
   * 0.3 - приходится всматриваться.
   */
  readonly contrast: number
  readonly timeLimitMs: number
}

const LEVELS: readonly LevelSpec[] = [
  { branches: 2, trials: 2, optionCount: 3, contrast: 1, timeLimitMs: 9000 },
  { branches: 2, trials: 2, optionCount: 3, contrast: 0.9, timeLimitMs: 8500 },
  { branches: 3, trials: 3, optionCount: 4, contrast: 0.8, timeLimitMs: 8000 },
  { branches: 3, trials: 3, optionCount: 4, contrast: 0.72, timeLimitMs: 7600 },
  { branches: 3, trials: 3, optionCount: 5, contrast: 0.65, timeLimitMs: 7200 },
  { branches: 4, trials: 3, optionCount: 5, contrast: 0.58, timeLimitMs: 6900 },
  { branches: 4, trials: 3, optionCount: 5, contrast: 0.52, timeLimitMs: 6600 },
  { branches: 4, trials: 3, optionCount: 6, contrast: 0.46, timeLimitMs: 6300 },
  { branches: 5, trials: 3, optionCount: 6, contrast: 0.41, timeLimitMs: 6000 },
  { branches: 5, trials: 3, optionCount: 6, contrast: 0.37, timeLimitMs: 5700 },
  { branches: 5, trials: 4, optionCount: 6, contrast: 0.33, timeLimitMs: 5500 },
  { branches: 6, trials: 4, optionCount: 6, contrast: 0.3, timeLimitMs: 5300 },
]

export const MIN_LEVEL = 1
export const MAX_LEVEL = LEVELS.length
export const INITIAL_LEVEL = 3

export const MIN_TIME_LIMIT_MS = 3500
export const MAX_TIME_LIMIT_MS = 14000

/** Эталонное время: рассмотреть форму дольше, чем нажать цвет. */
export const REFERENCE_RT_MS = 2600

export const SECTORS = 6

export function specForLevel(level: number): LevelSpec {
  const index = Math.min(Math.max(level, MIN_LEVEL), MAX_LEVEL) - 1
  const spec = LEVELS[index]
  if (!spec) throw new Error(`Snowflake: нет спецификации для уровня ${level}`)
  return spec
}

const TIPS: readonly TipKind[] = ['plain', 'fork', 'diamond']

function makeArm(branchCount: number, rng: Rng): Arm {
  const branches: Branch[] = []
  for (let i = 0; i < branchCount; i++) {
    // ветки распределяем по длине луча равномерно, но со сдвигом -
    // иначе все снежинки выглядят одинаково
    const base = (i + 1) / (branchCount + 1)
    branches.push({
      at: round2(base + (rng.next() - 0.5) * 0.12),
      len: round2(0.2 + rng.next() * 0.22),
      angle: Math.round(28 + rng.next() * 30),
    })
  }
  return { branches, tip: rng.pick(TIPS) }
}

/**
 * Сдвиг значения на заданный шаг в ту сторону, где есть запас.
 *
 * Наивный вариант (случайный знак плюс clamp) давал беду: если
 * значение уже у границы, зажим возвращал почти исходное число.
 * Вариант формально отличался, но разницу было физически не видно -
 * ответ превращался в угадывание. Выбираем направление по запасу.
 */
function nudge(value: number, step: number, min: number, max: number, rng: Rng): number {
  const roomUp = max - value
  const roomDown = value - min

  // обе стороны свободны - выбираем случайно, чтобы не было перекоса
  if (roomUp >= step && roomDown >= step) {
    return rng.next() < 0.5 ? value - step : value + step
  }
  if (roomUp >= step) return value + step
  if (roomDown >= step) return value - step

  // шаг не помещается ни туда, ни сюда - идём в сторону большего запаса
  return roomUp >= roomDown ? max : min
}

/** Изменённая копия луча. Чем меньше contrast, тем тоньше отличие. */
function mutate(arm: Arm, contrast: number, rng: Rng): Arm {
  const branches = arm.branches.map((b) => ({ ...b }))
  const kinds = ['len', 'angle', 'at', 'tip'] as const
  const kind = rng.pick(kinds)

  if (kind === 'tip') {
    const others = TIPS.filter((t) => t !== arm.tip)
    return { branches, tip: rng.pick(others) }
  }

  const i = rng.int(branches.length)
  const b = branches[i]
  if (!b) return { branches, tip: arm.tip }

  if (kind === 'len') {
    b.len = round2(nudge(b.len, 0.3 * contrast, 0.12, 0.5, rng))
  } else if (kind === 'angle') {
    b.angle = Math.round(nudge(b.angle, 34 * contrast, 15, 72, rng))
  } else {
    b.at = round2(nudge(b.at, 0.28 * contrast, 0.15, 0.9, rng))
  }

  return { branches, tip: arm.tip }
}

function round2(x: number): number {
  return Math.round(x * 100) / 100
}

/**
 * Насколько два луча различимы на глаз, в условных единицах.
 *
 * Каждый параметр нормирован своим диапазоном, поэтому вклады
 * сопоставимы. Нужна не для игры, а для проверки: варианты обязаны
 * отличаться заметно, иначе задача превращается в угадывание.
 */
export function armDistance(a: Arm, b: Arm): number {
  let d = a.tip !== b.tip ? 0.5 : 0
  const n = Math.min(a.branches.length, b.branches.length)
  for (let i = 0; i < n; i++) {
    const x = a.branches[i]
    const y = b.branches[i]
    if (!x || !y) continue
    d += Math.abs(x.at - y.at) / 0.75
    d += Math.abs(x.len - y.len) / 0.38
    d += Math.abs(x.angle - y.angle) / 57
  }
  return d
}

/** Порог различимости: ниже - разницу не видно, задача становится лотереей. */
export const MIN_DISTANCE = 0.08

/** Строковый слепок луча - чтобы отсеивать одинаковые варианты. */
export function armKey(arm: Arm): string {
  return arm.tip + '|' + arm.branches.map((b) => `${b.at}:${b.len}:${b.angle}`).join(',')
}

/**
 * @param pace личный темп игрока: лимит растягивается под тех,
 *   кто рассматривает дольше эталона.
 */
export function generateRound(level: number, rng: Rng, pace = 1): SnowflakeRound {
  const spec = specForLevel(level)

  const trials: SnowflakeTrial[] = []
  for (let t = 0; t < spec.trials; t++) {
    const arm = makeArm(spec.branches, rng)
    const seen = new Set([armKey(arm)])
    const distractors: Arm[] = []

    // отсеиваем совпадения: мутация могла попасть в исходные значения
    let guard = 0
    while (distractors.length < spec.optionCount - 1 && guard < 60) {
      guard++
      const candidate = mutate(arm, spec.contrast, rng)
      const key = armKey(candidate)
      if (seen.has(key)) continue
      // формально другой набор чисел ещё не значит «видно разницу»
      if (armDistance(arm, candidate) < MIN_DISTANCE) continue
      seen.add(key)
      distractors.push(candidate)
    }

    const options = rng.shuffle([arm, ...distractors])
    trials.push({
      arm,
      options,
      correctIndex: options.findIndex((o) => armKey(o) === armKey(arm)),
      missingSector: rng.int(SECTORS),
    })
  }

  const timeLimitMs = Math.round(
    Math.min(Math.max(spec.timeLimitMs * pace, MIN_TIME_LIMIT_MS), MAX_TIME_LIMIT_MS),
  )

  return { trials, timeLimitMs }
}

export function evaluate(round: SnowflakeRound, answer: SnowflakeAnswer): RoundOutcome {
  const total = round.trials.length
  if (total === 0) return { correct: false, accuracy: 0, rtMs: answer.rtMs }

  let hits = 0
  round.trials.forEach((trial, i) => {
    if (answer.picks[i] === trial.correctIndex) hits++
  })

  return {
    correct: hits === total,
    accuracy: hits / total,
    rtMs: answer.rtMs,
  }
}
